import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { SessionListResponse } from '../../interfaces/sessions';
import { Typography } from '../../interfaces/typography';
import { getSessionState } from '../../utils/session';

export interface SessionStateModalProps {
  session: SessionListResponse;
  isOpen: boolean;
  onClose: () => void;
  anchorElement?: HTMLElement | null;
  anchorRect?: DOMRect | null;
  position?: 'top' | 'bottom' | 'auto';
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * Reusable modal component for displaying session state fields with hover interactions.
 * Can be used by both SessionList and SessionHistory components.
 */
export const SessionStateModal: React.FC<SessionStateModalProps> = ({
  session,
  isOpen,
  onClose,
  anchorElement,
  anchorRect,
  position = 'auto',
  onMouseEnter,
  onMouseLeave
}) => {
  const [portalNode] = useState(() => {
    if (typeof document !== 'undefined') {
      const node = document.createElement('div');
      node.style.position = 'fixed';
      node.style.left = '0';
      node.style.top = '0';
      node.style.width = '100%';
      node.style.height = '0';
      node.style.overflow = 'visible';
      node.style.zIndex = '9999';
      return node;
    }
    return null;
  });

  const modalRef = useRef<HTMLDivElement>(null);

  // Handle portal mounting
  useEffect(() => {
    if (portalNode) {
      document.body.appendChild(portalNode);
      return () => {
        document.body.removeChild(portalNode);
      };
    }
  }, [portalNode]);

  // Handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Calculate modal position
  const getModalPosition = () => {
    const rect = anchorRect || (anchorElement ? anchorElement.getBoundingClientRect() : null);
    
    if (!rect) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    const modalWidth = 280;
    const modalHeight = 300;
    
    let top = rect.bottom - 2; // Slight overlap to ensure no gap
    let left = Math.max(8, Math.min(rect.left, window.innerWidth - modalWidth - 8));
    
    // Auto position based on available space
    if (position === 'auto') {
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < modalHeight && spaceAbove > spaceBelow) {
        top = rect.top - modalHeight + 2; // Slight overlap
      }
    } else if (position === 'top') {
      top = rect.top - modalHeight + 2; // Slight overlap
    }

    return { top: `${top}px`, left: `${left}px` };
  };

  // Extract session state fields (filter out webhook status fields)
  const sessionStateFields = React.useMemo(() => {
    if (!session || !session.result) return [];

    const states = getSessionState(session);
    return Object.entries(states)
      .filter(([key]) => !(key.startsWith('__webhook_') && key.endsWith('__')))
      .map(([key, value]) => ({
        key,
        value
      }));
  }, [session]);

  if (!isOpen || !portalNode || sessionStateFields.length === 0) {
    return null;
  }

  const modalPosition = getModalPosition();

  const modalContent = (
    <div 
      ref={modalRef}
      className="fixed bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] w-[280px] max-h-[300px] overflow-y-auto"
      style={modalPosition}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={(e) => {
        e.stopPropagation();
        onMouseEnter?.();
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        onMouseLeave?.();
      }}
    >
      <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex justify-between items-center">
        <Typography variant="h5" fontWeight="700" color="#111827">
          Session State
        </Typography>
        <button 
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X size={16} className="text-gray-500" />
        </button>
      </div>
      
      <div className="p-4">
        <div className="space-y-3">
          {sessionStateFields.map((field, index) => (
            <div 
              key={index}
              className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors border border-gray-200"
            >
              <div className="space-y-2">
                <div>
                  <Typography variant="smallParagraph" fontWeight="600" color="#374151">
                    {field.key}
                  </Typography>
                </div>
                <div>
                  <Typography variant="smallParagraph" fontWeight="500" color="#1f2937">
                    {field.value}
                  </Typography>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {sessionStateFields.length === 0 && (
          <div className="text-center py-8">
            <Typography variant="mediumParagraph" color="#9ca3af">
              No session state data available
            </Typography>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, portalNode);
};